<template>
  <div class="client-management">
    <!-- Header -->
    <div class="page-header">
      <div class="header-left">
        <h1><i class="fas fa-users-cog"></i> Client Management</h1>
        <p class="header-subtitle">Manage all onboarded compliance clients — setup status, health, and quick actions.</p>
      </div>
      <div class="header-actions">
        <router-link to="/setup/compliance-client" class="btn btn-success">
          <i class="fas fa-plus-circle"></i> Onboard New Client
        </router-link>
      </div>
    </div>

    <!-- Summary Cards -->
    <div class="summary-cards" v-if="summary">
      <div class="summary-card">
        <div class="card-icon blue"><i class="fas fa-building"></i></div>
        <div class="card-content">
          <div class="card-value">{{ summary.total_organizations }}</div>
          <div class="card-label">Total Clients</div>
        </div>
      </div>
      <div class="summary-card">
        <div class="card-icon green"><i class="fas fa-check-circle"></i></div>
        <div class="card-content">
          <div class="card-value">{{ summary.setup_complete }}</div>
          <div class="card-label">Fully Setup</div>
        </div>
      </div>
      <div class="summary-card">
        <div class="card-icon orange"><i class="fas fa-exclamation-circle"></i></div>
        <div class="card-content">
          <div class="card-value">{{ summary.setup_partial }}</div>
          <div class="card-label">Needs Attention</div>
        </div>
      </div>
      <div class="summary-card">
        <div class="card-icon purple"><i class="fas fa-id-card-alt"></i></div>
        <div class="card-content">
          <div class="card-value">{{ summary.total_entities }}</div>
          <div class="card-label">Total Entities</div>
        </div>
      </div>
      <div class="summary-card">
        <div class="card-icon teal"><i class="fas fa-user-friends"></i></div>
        <div class="card-content">
          <div class="card-value">{{ summary.total_users }}</div>
          <div class="card-label">Total Users</div>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="filters-bar">
      <div class="search-box">
        <i class="fas fa-search"></i>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search by name or email..."
          @input="debouncedSearch"
        >
      </div>
      <select v-model="statusFilter" @change="loadOrganizations" class="filter-select">
        <option value="">All Statuses</option>
        <option value="active">Active</option>
        <option value="inactive">Inactive</option>
        <option value="trial">Trial</option>
      </select>
      <select v-model="tierFilter" @change="loadOrganizations" class="filter-select">
        <option value="">All Tiers</option>
        <option value="ENTERPRISE">Enterprise</option>
        <option value="PROFESSIONAL">Professional</option>
        <option value="STANDARD">Standard</option>
        <option value="TRIAL">Trial</option>
      </select>
      <select v-model="sortBy" @change="loadOrganizations" class="filter-select">
        <option value="-created_at">Newest First</option>
        <option value="created_at">Oldest First</option>
        <option value="name">Name A-Z</option>
        <option value="-name">Name Z-A</option>
        <option value="-entity_count">Most Entities</option>
      </select>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-state">
      <i class="fas fa-spinner fa-spin fa-2x"></i>
      <p>Loading clients...</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="organizations.length === 0" class="empty-state">
      <i class="fas fa-building fa-3x"></i>
      <h3>No Clients Yet</h3>
      <p>Get started by onboarding your first compliance client.</p>
      <router-link to="/setup/compliance-client" class="btn btn-primary">
        <i class="fas fa-rocket"></i> Onboard First Client
      </router-link>
    </div>

    <!-- Client Table -->
    <div v-else class="client-table-wrapper">
      <table class="client-table">
        <thead>
          <tr>
            <th>Organization</th>
            <th>Status</th>
            <th>Setup</th>
            <th class="text-center">Projects</th>
            <th class="text-center">Users</th>
            <th class="text-center">Entities</th>
            <th>Tier</th>
            <th>Onboarded</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="org in organizations"
            :key="org.id"
            :class="{ inactive: !org.is_active }"
            @click="openDetail(org)"
          >
            <td class="org-name-cell">
              <div class="org-name">{{ org.name }}</div>
              <div class="org-email">{{ org.primary_contact_email }}</div>
            </td>
            <td>
              <span :class="['status-badge', org.is_active ? 'active' : 'inactive']">
                {{ org.is_active ? 'Active' : 'Inactive' }}
              </span>
            </td>
            <td>
              <span :class="['setup-badge', 'setup-' + org.setup_status]">
                <i :class="setupIcon(org.setup_status)"></i>
                {{ setupLabel(org.setup_status) }}
              </span>
            </td>
            <td class="text-center">{{ org.project_count }}</td>
            <td class="text-center">{{ org.user_count }}</td>
            <td class="text-center">
              <span class="entity-count" :class="{ 'has-entities': org.entity_count > 0 }">
                {{ org.entity_count.toLocaleString() }}
              </span>
            </td>
            <td>
              <span :class="['tier-badge', 'tier-' + org.subscription_tier.toLowerCase()]">
                {{ org.subscription_tier }}
              </span>
            </td>
            <td class="date-cell">{{ formatDate(org.created_at) }}</td>
            <td class="actions-cell" @click.stop>
              <button @click="openDetail(org)" class="btn-action" title="View details">
                <i class="fas fa-eye"></i>
              </button>
              <router-link
                :to="'/compliance-health'"
                class="btn-action"
                title="Compliance dashboard"
              >
                <i class="fas fa-heartbeat"></i>
              </router-link>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Detail Panel (slide-in) -->
    <transition name="slide">
      <div v-if="selectedOrg" class="detail-overlay" @click.self="closeDetail">
        <div class="detail-panel">
          <div class="detail-header">
            <h2>{{ selectedOrgDetail?.organization?.name || selectedOrg.name }}</h2>
            <button @click="closeDetail" class="btn-close"><i class="fas fa-times"></i></button>
          </div>

          <div v-if="loadingDetail" class="detail-loading">
            <i class="fas fa-spinner fa-spin"></i> Loading details...
          </div>

          <div v-else-if="selectedOrgDetail" class="detail-body">
            <!-- Setup Checklist -->
            <div class="detail-section">
              <h3>
                <i class="fas fa-clipboard-check"></i> Setup Checklist
                <span class="checklist-score" :class="'score-' + selectedOrgDetail.checklist.status">
                  {{ selectedOrgDetail.checklist.passed }}/{{ selectedOrgDetail.checklist.total }}
                  ({{ selectedOrgDetail.checklist.score }}%)
                </span>
              </h3>
              <div class="checklist-items">
                <div
                  v-for="(item, idx) in selectedOrgDetail.checklist.items"
                  :key="idx"
                  :class="['checklist-item', { passed: item.passed, failed: !item.passed }]"
                >
                  <i :class="item.passed ? 'fas fa-check-circle' : 'fas fa-times-circle'"></i>
                  <span>{{ item.label }}</span>
                  <span class="checklist-category">{{ item.category }}</span>
                </div>
              </div>
            </div>

            <!-- Projects -->
            <div class="detail-section">
              <h3><i class="fas fa-folder-open"></i> Projects ({{ selectedOrgDetail.projects.length }})</h3>
              <div v-if="selectedOrgDetail.projects.length === 0" class="empty-mini">No projects yet</div>
              <div v-for="proj in selectedOrgDetail.projects" :key="proj.id" class="project-card">
                <div class="project-card-header">
                  <strong>{{ proj.name }}</strong>
                  <span class="badge badge-type">{{ proj.project_type }}</span>
                </div>
                <div class="project-card-stats">
                  <span><i class="fas fa-id-card"></i> {{ proj.entity_count }} entities</span>
                  <span v-if="proj.check_definition">
                    <i class="fas fa-clipboard-check"></i> {{ proj.check_definition.requirements_count }} requirements
                  </span>
                  <span v-else class="text-warning"><i class="fas fa-exclamation-triangle"></i> No check def</span>
                </div>
                <div v-if="proj.check_definition && proj.check_definition.client_config_summary" class="config-summary">
                  <small>
                    Client: {{ proj.check_definition.client_config_summary.client_name || '—' }}
                    | Preset: {{ proj.check_definition.client_config_summary.preset_applied || '—' }}
                  </small>
                </div>
              </div>
            </div>

            <!-- Users -->
            <div class="detail-section">
              <h3><i class="fas fa-users"></i> Users ({{ selectedOrgDetail.users.length }})</h3>
              <div v-if="selectedOrgDetail.users.length === 0" class="empty-mini">No users yet</div>
              <table v-else class="users-mini-table">
                <thead>
                  <tr><th>Name</th><th>Email</th><th>Role</th><th>Last Login</th></tr>
                </thead>
                <tbody>
                  <tr v-for="user in selectedOrgDetail.users" :key="user.id" :class="{ inactive: !user.is_active }">
                    <td>{{ user.first_name }} {{ user.last_name }}</td>
                    <td>{{ user.email }}</td>
                    <td><span class="role-badge">{{ user.role }}</span></td>
                    <td>{{ user.last_login ? formatDate(user.last_login) : 'Never' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Quick Tips -->
            <div class="detail-section tips-section">
              <h3><i class="fas fa-lightbulb"></i> Next Steps</h3>
              <div class="tips-list">
                <div v-if="!selectedOrgDetail.checklist.items.find(i => i.label.includes('Entities') && i.passed)" class="tip">
                  <i class="fas fa-arrow-right"></i> Import entities via CSV from the Compliance Health dashboard
                </div>
                <div v-if="!selectedOrgDetail.checklist.items.find(i => i.label.includes('Client-specific') && i.passed)" class="tip">
                  <i class="fas fa-arrow-right"></i> Customize check definition limits in the Check Lab
                </div>
                <div v-if="selectedOrgDetail.users.length < 2" class="tip">
                  <i class="fas fa-arrow-right"></i> Consider adding more users for this organization
                </div>
                <div v-if="selectedOrgDetail.checklist.score === 100" class="tip tip-success">
                  <i class="fas fa-check"></i> Setup is complete — this client is ready to go!
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Toast -->
    <transition name="toast">
      <div v-if="toast" class="toast-notification" :class="toast.type">
        <i :class="toast.type === 'error' ? 'fas fa-exclamation-circle' : 'fas fa-check-circle'"></i>
        {{ toast.message }}
      </div>
    </transition>
  </div>
</template>

<script>
import api from '@/services/api'
import { debounce } from 'lodash-es'

export default {
  name: 'ClientManagementDashboard',

  data() {
    return {
      organizations: [],
      summary: null,
      loading: false,

      // Filters
      searchQuery: '',
      statusFilter: '',
      tierFilter: '',
      sortBy: '-created_at',

      // Detail panel
      selectedOrg: null,
      selectedOrgDetail: null,
      loadingDetail: false,

      // UI
      toast: null,
      debouncedSearch: null,
    }
  },

  created() {
    this.debouncedSearch = debounce(this.loadOrganizations, 300)
  },

  async mounted() {
    await this.loadOrganizations()
  },

  methods: {
    async loadOrganizations() {
      this.loading = true
      try {
        const params = {
          sort_by: this.sortBy,
        }
        if (this.searchQuery) params.search = this.searchQuery
        if (this.statusFilter) params.status = this.statusFilter
        if (this.tierFilter) params.tier = this.tierFilter

        const response = await api.get('/client-management/overview/', { params })
        this.organizations = response.data.organizations || []
        this.summary = response.data.summary || {}
      } catch (error) {
        console.error('Failed to load organizations:', error)
        this.showToast('error', 'Failed to load client data')
      } finally {
        this.loading = false
      }
    },

    async openDetail(org) {
      this.selectedOrg = org
      this.loadingDetail = true
      this.selectedOrgDetail = null

      try {
        const response = await api.get(`/client-management/${org.id}/`)
        this.selectedOrgDetail = response.data
      } catch (error) {
        console.error('Failed to load org detail:', error)
        this.showToast('error', 'Failed to load organization details')
      } finally {
        this.loadingDetail = false
      }
    },

    closeDetail() {
      this.selectedOrg = null
      this.selectedOrgDetail = null
    },

    setupIcon(status) {
      const icons = {
        complete: 'fas fa-check-circle',
        partial: 'fas fa-exclamation-circle',
        not_started: 'fas fa-times-circle',
      }
      return icons[status] || icons.not_started
    },

    setupLabel(status) {
      const labels = {
        complete: 'Complete',
        partial: 'Partial',
        not_started: 'Not Started',
      }
      return labels[status] || 'Unknown'
    },

    formatDate(dateStr) {
      if (!dateStr) return '—'
      const d = new Date(dateStr)
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    },

    showToast(type, message) {
      this.toast = { type, message }
      setTimeout(() => { this.toast = null }, 4000)
    },
  },
}
</script>

<style scoped>
/* ===== Layout ===== */
.client-management {
  max-width: 1300px;
  margin: 0 auto;
  padding: 24px;
}

/* ===== Header ===== */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 28px;
}

.page-header h1 {
  margin: 0 0 4px 0;
  font-size: 1.8rem;
  color: #1a1a2e;
}

.header-subtitle {
  color: #6b7280;
  margin: 0;
  font-size: 0.95rem;
}

/* ===== Summary Cards ===== */
.summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.summary-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #f3f4f6;
}

.card-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  color: white;
  flex-shrink: 0;
}

.card-icon.blue { background: linear-gradient(135deg, #667eea, #764ba2); }
.card-icon.green { background: linear-gradient(135deg, #10b981, #059669); }
.card-icon.orange { background: linear-gradient(135deg, #f59e0b, #d97706); }
.card-icon.purple { background: linear-gradient(135deg, #8b5cf6, #7c3aed); }
.card-icon.teal { background: linear-gradient(135deg, #14b8a6, #0d9488); }

.card-value {
  font-size: 1.6rem;
  font-weight: 700;
  color: #1a1a2e;
  line-height: 1;
}

.card-label {
  font-size: 0.8rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-top: 2px;
}

/* ===== Filters ===== */
.filters-bar {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.search-box {
  flex: 1;
  min-width: 240px;
  position: relative;
}

.search-box i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
}

.search-box input {
  width: 100%;
  padding: 10px 14px 10px 36px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: border-color 0.2s;
}

.search-box input:focus {
  outline: none;
  border-color: #667eea;
}

.filter-select {
  padding: 10px 14px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.85rem;
  background: white;
  cursor: pointer;
}

/* ===== Table ===== */
.client-table-wrapper {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  border: 1px solid #f3f4f6;
}

.client-table {
  width: 100%;
  border-collapse: collapse;
}

.client-table thead {
  background: #f9fafb;
}

.client-table th {
  padding: 12px 16px;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #e5e7eb;
}

.client-table td {
  padding: 14px 16px;
  font-size: 0.9rem;
  border-bottom: 1px solid #f3f4f6;
  color: #374151;
}

.client-table tbody tr {
  cursor: pointer;
  transition: background 0.15s;
}

.client-table tbody tr:hover {
  background: #f9fafb;
}

.client-table tbody tr.inactive {
  opacity: 0.6;
}

.text-center {
  text-align: center;
}

.org-name-cell .org-name {
  font-weight: 600;
  color: #1a1a2e;
}

.org-name-cell .org-email {
  font-size: 0.8rem;
  color: #9ca3af;
}

.date-cell {
  font-size: 0.8rem;
  color: #6b7280;
  white-space: nowrap;
}

/* Badges */
.status-badge {
  padding: 3px 10px;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 600;
}

.status-badge.active {
  background: #ecfdf5;
  color: #059669;
}

.status-badge.inactive {
  background: #fef2f2;
  color: #dc2626;
}

.setup-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 600;
}

.setup-complete {
  background: #ecfdf5;
  color: #059669;
}

.setup-partial {
  background: #fffbeb;
  color: #d97706;
}

.setup-not_started {
  background: #fef2f2;
  color: #dc2626;
}

.tier-badge {
  padding: 3px 10px;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.tier-enterprise { background: #ede9fe; color: #7c3aed; }
.tier-professional { background: #dbeafe; color: #2563eb; }
.tier-standard { background: #f3f4f6; color: #6b7280; }
.tier-trial { background: #fef3c7; color: #92400e; }
.tier-platform { background: #e0e7ff; color: #4338ca; }

.entity-count.has-entities {
  font-weight: 600;
  color: #059669;
}

.actions-cell {
  display: flex;
  gap: 6px;
}

.btn-action {
  background: none;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 6px 8px;
  cursor: pointer;
  color: #6b7280;
  font-size: 0.85rem;
  transition: all 0.15s;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
}

.btn-action:hover {
  background: #f3f4f6;
  color: #374151;
  border-color: #d1d5db;
}

/* ===== Detail Panel ===== */
.detail-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 1000;
  display: flex;
  justify-content: flex-end;
}

.detail-panel {
  width: 580px;
  max-width: 90vw;
  background: white;
  height: 100%;
  overflow-y: auto;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 2px solid #f3f4f6;
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
}

.detail-header h2 {
  margin: 0;
  font-size: 1.3rem;
  color: #1a1a2e;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.2rem;
  color: #9ca3af;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
}

.btn-close:hover {
  background: #f3f4f6;
  color: #374151;
}

.detail-loading {
  padding: 40px;
  text-align: center;
  color: #6b7280;
}

.detail-body {
  padding: 24px;
  flex: 1;
}

.detail-section {
  margin-bottom: 28px;
}

.detail-section h3 {
  margin: 0 0 14px 0;
  font-size: 1rem;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Checklist */
.checklist-score {
  margin-left: auto;
  font-size: 0.8rem;
  padding: 2px 10px;
  border-radius: 10px;
  font-weight: 700;
}

.score-complete { background: #ecfdf5; color: #059669; }
.score-partial { background: #fffbeb; color: #d97706; }
.score-not_started { background: #fef2f2; color: #dc2626; }

.checklist-items {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.checklist-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 0.85rem;
}

.checklist-item.passed {
  background: rgba(16, 185, 129, 0.06);
  color: #059669;
}

.checklist-item.failed {
  background: rgba(239, 68, 68, 0.06);
  color: #dc2626;
}

.checklist-category {
  margin-left: auto;
  font-size: 0.7rem;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

/* Project Cards */
.project-card {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 10px;
}

.project-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.project-card-header strong {
  color: #1a1a2e;
  font-size: 0.9rem;
}

.project-card-stats {
  display: flex;
  gap: 16px;
  font-size: 0.8rem;
  color: #6b7280;
}

.project-card-stats i {
  margin-right: 4px;
}

.text-warning {
  color: #d97706;
}

.config-summary {
  margin-top: 6px;
  font-size: 0.8rem;
  color: #9ca3af;
}

/* Users Mini Table */
.users-mini-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
}

.users-mini-table th {
  text-align: left;
  padding: 6px 10px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  font-size: 0.7rem;
  border-bottom: 1px solid #e5e7eb;
}

.users-mini-table td {
  padding: 8px 10px;
  border-bottom: 1px solid #f3f4f6;
  color: #374151;
}

.users-mini-table tr.inactive td {
  opacity: 0.5;
}

.role-badge {
  background: #dbeafe;
  color: #1d4ed8;
  padding: 2px 8px;
  border-radius: 8px;
  font-size: 0.7rem;
  font-weight: 600;
}

/* Tips */
.tips-section {
  background: #eff6ff;
  border-radius: 10px;
  padding: 16px;
  border: 1px solid #bfdbfe;
}

.tips-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tip {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  color: #1d4ed8;
}

.tip-success {
  color: #059669;
}

/* Misc */
.badge {
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 600;
}

.badge-type {
  background: #dbeafe;
  color: #1d4ed8;
  text-transform: uppercase;
}

.empty-state,
.loading-state {
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
}

.empty-state i,
.loading-state i {
  display: block;
  margin-bottom: 12px;
  color: #d1d5db;
}

.empty-state h3 {
  color: #374151;
  margin: 0 0 8px 0;
}

.empty-mini {
  color: #9ca3af;
  font-size: 0.85rem;
  font-style: italic;
  padding: 12px;
}

/* Buttons */
.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  text-decoration: none;
}

.btn-success {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.btn-success:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover {
  background: #5a6fd8;
}

/* Animations */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter-from .detail-panel,
.slide-leave-to .detail-panel {
  transform: translateX(100%);
}

.slide-enter-from,
.slide-leave-to {
  opacity: 0;
}

/* Toast */
.toast-notification {
  position: fixed;
  bottom: 24px;
  right: 24px;
  padding: 14px 20px;
  border-radius: 10px;
  color: white;
  font-weight: 600;
  z-index: 1100;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  font-size: 0.9rem;
}

.toast-notification.success { background: #10b981; }
.toast-notification.error { background: #ef4444; }

.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateY(16px);
}

/* ===== Responsive ===== */
@media (max-width: 768px) {
  .client-management {
    padding: 16px;
  }

  .page-header {
    flex-direction: column;
    gap: 12px;
  }

  .summary-cards {
    grid-template-columns: repeat(2, 1fr);
  }

  .filters-bar {
    flex-direction: column;
  }

  .client-table-wrapper {
    overflow-x: auto;
  }

  .detail-panel {
    width: 100%;
  }
}
</style>
